import {
  MutableRefObject,
  RefObject,
  useEffect,
  useRef,
  useState,
} from 'react';
import { IBMLocale } from 'src/common/locale/mapValidLocale';
import { useSmallBreakpoint } from 'src/common/hooks/widgets/useBreakpoint';
import { useEnv } from 'src/common/hooks/widgets/useEnv';
import { useCookiesURL } from 'src/common/hooks/widgets/useCookiesURL';
import * as URX_FORM from '../constants/urx-form';
import {
  UT30PASaaS,
  EST_DEF_QUERIES,
  EST_DEF_RECORDS,
  EST_DEF_FILTERCHAINS,
  EST_DEF_MONITORS,
} from '../constants/spread';
import { Helmet } from 'react-helmet';
import {
  Button,
  ButtonSet,
  ModalWrapper,
  NumberInput,
  SkeletonPlaceholder,
  Tooltip,
  UnorderedList,
  ListItem,
  Checkbox,
  Link,
  RadioButtonGroup,
  RadioButton,
} from 'carbon-components-react';
import './NS1PriceEstimator.scss';
import 'src/common/style/common.scss';
import { usePrice } from '../hooks/usePrice';
import Cookies from 'js-cookie';
import Footnote from './Footnote';
import { FormModal } from 'src/common/utils/FormModal';
import Thanks from './Thanks';
import { segmentsTracking } from '../lib/tracker';
import { initUrxForm } from 'src/common/hooks/widgets/urxForm';
import { swapCountryAndLanguage } from 'src/common/utils/swapCountryAndLanguage';
import { useBPName } from 'src/common/hooks/widgets/useBPName';
// import countryList from '../services/currencies.json';
import BusinessPartner from 'src/common/components/BusinessPartner/BusinessPartner';
import {
  LangDefaults,
  determineCountry,
  useLang,
} from 'src/common/hooks/widgets/useLang';
import DisclaimerText from 'src/common/components/DisclaimerText/DisclaimerText';
import { ArrowRight16, CheckmarkOutline20 } from '@carbon/icons-react';
import OmniChannel from './OmniChannel/OmniChannel';
import OmniChannelRequest from './OmniChannel/OmniChannelRequest';
import { createUseTranslation } from '../../../common/hooks/widgets/useTranslation';
import { useNS1PESelector } from '../lib/redux/hooks';
import messages from '../locales/messages';
import { shortcode } from '../NS1PE.widget';
import { convertToString } from '../lib/utils';

export interface NS1PriceEstimatorProps {
  langCode: string;
  test: boolean;
  countryToTest: string;
  useVersion2: string;
}

const NS1PriceEstimatorNew = ({
  langCode,
  test,
  countryToTest,
  useVersion2,
}: NS1PriceEstimatorProps) => {
  const localeCode = swapCountryAndLanguage(langCode as IBMLocale);
  const [urxlang] = useState(localeCode.toString());
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [currency, setCurrency] = useState('');
  const [country, setCountry] = useState('');
  const AEMMessages = useNS1PESelector((state) => state.translations.messages);
  const useTranslation = createUseTranslation(messages, AEMMessages);
  const [showPrice, setShowPrice] = useState(false);
  // lang and userlocale is based out of ddo.
  const [lang, userLocale, ddoCountry, windowOpen] = useLang(
    null, // null since countryList to be fetch via COS
    test,
    countryToTest,
    setCurrency,
    setCountry,
    localeCode,
    shortcode, // widget short code to fetch countries via COS
  );

  const [localCurrency, setlocalCurrency] = useState<string>(localeCode);
  useEffect(() => {
    setlocalCurrency(userLocale); // show local Currency
  }, [userLocale, localeCode, localCurrency]);

  const [localExist, setLocalExist] = useState(false);
  let localStorageFound = localStorage.getItem('langLocale');
  if (localStorageFound === null)
    localStorageFound = LangDefaults.localCountryLang;
  const [selectedLocale, setSelectedLocale] = useState(localStorageFound);

  const [toggleCountry, setToggleCountry] = useState(false);
  const [modalLocalCountry, setModalLocalCountry] = useState('');
  const [modalLocalCurrency, setModalLocalCurrency] = useState('USD');
  const [modalLocalLanguage, setModalLocalLanguage] = useState('English');
  const [omnichannel, setOmniChannel] = useState(false);
  const [omnichannelRequest, setOmniChannelRequest] = useState(false);
  const [closeModal, setCloseModal] = useState(false);
  const [formId, setFormId] = useState('');

  // Determine Country
  determineCountry(
    setLocalExist,
    setSelectedLocale,
    setModalLocalCountry,
    setModalLocalLanguage,
    setModalLocalCurrency,
    setToggleCountry,
    lang,
    null, // null since countryList to be fetched via COS
    true,
    test,
    localeCode,
    shortcode, // widget short code to fetch countries via COS
  );

  const isSmall = useSmallBreakpoint('560px');
  const [environment] = useEnv();
  const [formComplete, setFormComplete] = useState(false);
  const [tileValueSelected, setTileValueSelected] = useState('essentials');
  const [packageName, setPackageName] = useState('Essentials');
  const [queriesInput, setQueriesInput] = useState(EST_DEF_QUERIES);
  const [recordsInput, setRecordsInput] = useState(EST_DEF_RECORDS);
  const [filterChainsInput, setFilterChainsInput] =
    useState(EST_DEF_FILTERCHAINS);
  const [monitorsInput, setMonitorsInput] = useState(EST_DEF_MONITORS);
  const [httpsRedirectInput, setHttpsRedirectInput] = useState(5);
  const [dataSet, setDataSet] = useState(false);
  const [zoneBackUp, setZoneBackUp] = useState(false);
  const [expertsLabs, setExpertsLabs] = useState(false);
  const [dedicatedDNS, setDedicatedDNS] = useState(false);
  const [dnsInsight, setDnsInsight] = useState(false);
  const [pulsar, setPulsar] = useState(false);
  const [advSupport, setAdvSupport] = useState(false);
  const [spikeProtection, setSpikeProtection] = useState(false);
  const queriesRef = useRef() as MutableRefObject<HTMLInputElement>;
  const recordsRef = useRef() as MutableRefObject<HTMLInputElement>;
  const filterChainsRef = useRef() as MutableRefObject<HTMLInputElement>;
  const monitorsRef = useRef() as MutableRefObject<HTMLInputElement>;
  const httpsRef = useRef() as MutableRefObject<HTMLInputElement>;
  const cookieURL = useCookiesURL(shortcode);
  const [notifToShow, setNotifToShow] = useState(false);
  const [priceAction, setPriceAction] = useState('Monthly');
  const priceActionRef = useRef() as RefObject<RadioButtonGroup>;
  const [totalCost, totalCostMonthly, loading] = usePrice({
    currency,
    country,
    queriesInput,
    recordsInput,
    filterChainsInput,
    monitorsInput,
    httpsRedirectInput,
    tileValueSelected,
    localeCode,
  });
  const learnMoreLink =
    'https://www.ibm.com/docs/' +
    (selectedLocale.split('-')[1] ? selectedLocale.split('-')[1] : 'en') +
    '/ns1-connect?topic=introduction-overview';
  const { bpName, logo, companyId, desc } = useBPName(UT30PASaaS, true);

  // Feature lists
  const freeFeatures = [
    [
      `${convertToString(useTranslation('number_queries_text', true))}: 5`,
      `${convertToString(useTranslation('number_records_text', true))}: 500`,
      `${convertToString(useTranslation('number_filterchains_text', true))}: 1`,
      `${convertToString(useTranslation('number_monitors_text', true))}: 2`,
      `${convertToString(useTranslation('https_field_text', true))}: 1`,
    ],
    [
      convertToString(useTranslation('activity_text', true)),
      'DNSSEC',
      convertToString(useTranslation('http_text', true)),
    ],
  ];
  const essentialsFeatures = [
    [
      `${convertToString(
        useTranslation('number_queries_text', true),
      )}: ${Math.max(30, Number(queriesRef?.current?.value) || 30)}`,
      `${convertToString(useTranslation('number_records_text', true))}: 1000`,
      `${convertToString(useTranslation('number_filterchains_text', true))}: 1`,
      `${convertToString(useTranslation('number_monitors_text', true))}: 2`,
      `${convertToString(useTranslation('https_field_text', true))}: 5`,
    ],
    [
      convertToString(useTranslation('activity_text', true)),
      convertToString(useTranslation('checkbox1_text', true)),
      'DNSSEC',
      convertToString(useTranslation('http_text', true)),
    ],
  ];
  const standardFeatures = [
    [
      `${convertToString(
        useTranslation('number_queries_text', true),
      )}: ${Math.max(50, Number(queriesRef?.current?.value) || 50)}`,
      `${convertToString(
        useTranslation('number_records_text', true),
      )}: ${Math.max(1000, Number(recordsRef?.current?.value) || 1000)}`,
      `${convertToString(
        useTranslation('number_filterchains_text', true),
      )}: ${Math.max(1, Number(filterChainsRef?.current?.value) || 1)}`,
      `${convertToString(
        useTranslation('number_monitors_text', true),
      )}: ${Math.max(2, Number(monitorsRef?.current?.value) || 2)}`,
      `${convertToString(useTranslation('https_field_text', true))}: 50`,
    ],
    [
      convertToString(useTranslation('activity_text', true)),
      convertToString(useTranslation('checkbox1_text', true)),
      convertToString(useTranslation('checkbox2_text', true)),
      'DNSSEC',
      convertToString(useTranslation('http_text', true)),
    ],
  ];
  const premiumFeatures = [
    [
      `${convertToString(
        useTranslation('number_queries_text', true),
      )}: ${convertToString(useTranslation('custom_text', true))}`,
      `${convertToString(
        useTranslation('number_records_text', true),
      )}: ${convertToString(useTranslation('custom_text', true))}`,
      `${convertToString(
        useTranslation('number_filterchains_text', true),
      )}: ${convertToString(useTranslation('custom_text', true))}`,
      `${convertToString(
        useTranslation('number_monitors_text', true),
      )}: ${convertToString(useTranslation('custom_text', true))}`,
      `${convertToString(
        useTranslation('https_field_text', true),
      )}: ${convertToString(useTranslation('unlimited_text', true))}`,
    ],
    [
      convertToString(useTranslation('activity_text', true)),
      convertToString(useTranslation('checkbox1_text', true)),
      'DNSSEC',
      convertToString(useTranslation('checkbox2_text', true)),
      convertToString(useTranslation('http_text', true)),
      convertToString(useTranslation('checkbox3_text', true)),
    ],
  ];

  function renderFeaturesList(
    tileValueSelected: string,
    pkgLabel: string,
  ): JSX.Element {
    let featuresToRender: string[] = [];
    let featuresAdditional: string[] = [];

    switch (tileValueSelected) {
      case 'free':
        featuresToRender = freeFeatures[0];
        featuresAdditional = freeFeatures[1];
        break;
      case 'essentials':
        featuresToRender = essentialsFeatures[0];
        featuresAdditional = essentialsFeatures[1];
        break;
      case 'standard':
        featuresToRender = standardFeatures[0];
        featuresAdditional = standardFeatures[1];
        break;
      case 'premium':
        featuresToRender = premiumFeatures[0];
        featuresAdditional = premiumFeatures[1];
        break;
      default:
        featuresToRender = [];
    }
    return (
      <>
        <p className="result_body_text pa-mar6 pa-marB2">
          <strong className="text-bold">
            {pkgLabel} {useTranslation('includes_text')}:
          </strong>
        </p>
        <UnorderedList className="ns1-features-list input-row-spacing">
          {featuresToRender.map((feature) => {
            const [label, value] = feature.split(': ');
            return (
              <ListItem key={label}>
                <CheckmarkOutline20 />
                {label}: <span className="text-bold">{value}</span>
              </ListItem>
            );
          })}
        </UnorderedList>
        <p className="result_body_text pa-mar6 pa-marB2">
          <strong className="text-bold">
            {useTranslation('include_text')}:
          </strong>
        </p>
        <div className="result_body_text pa-mar1 other-features">
          {featuresAdditional.map((feature, idx) => (
            <span key={feature}>
              {feature}
              {idx < featuresAdditional.length - 1 ? ', ' : ''}
            </span>
          ))}
        </div>
      </>
    );
  }

  window.urxEnvironment = environment;
  window.onUrxFormSubmitSuccessMultiple =
    window.onUrxFormSubmitSuccessMultiple || {};
  window.onUrxFormSubmitSuccessMultiple[URX_FORM.INSTANCE_ID] = function (
    param,
  ) {
    setFormComplete(!formComplete);
    setDrawerOpen(false); // Hide Drawer on Thank you page
    setCloseModal(true);
    if (param.targetUrl && param.targetUrl.trim() != '') {
      const url = new URL(param.targetUrl.trim());
      if (url.protocol === 'http:' || url.protocol === 'https:') {
        window.location.href = url.href;
      }
    }
  };

  useEffect(() => {
    setCookie(formId);
  }, [
    queriesInput,
    recordsInput,
    filterChainsInput,
    monitorsInput,
    httpsRedirectInput,
    totalCostMonthly,
    totalCost,
    packageName,
    formId,
  ]);

  useEffect(() => {
    if (
      tileValueSelected === 'essentials' ||
      tileValueSelected === 'standard'
    ) {
      setShowPrice(true);
    } else {
      setShowPrice(false);
    }
  }, [
    queriesInput,
    recordsInput,
    monitorsInput,
    filterChainsInput,
    httpsRedirectInput,
    dataSet,
    zoneBackUp,
    expertsLabs,
    dedicatedDNS,
    dnsInsight,
    pulsar,
    advSupport,
    spikeProtection,
    tileValueSelected,
  ]);

  const setCookie = (formId?: string) => {
    Cookies.set(
      'urxdcq',
      JSON.stringify({
        source: formId,
        // One field to provide all values (stringify)
        dcq: {
          Q_NOTESDATA:
            cookieURL +
            ' Queries: ' +
            queriesInput +
            ' | Records: ' +
            recordsInput +
            ' | Filter chains: ' +
            filterChainsInput +
            ' | Monitors: ' +
            monitorsInput +
            ' | HTTP redirects: ' +
            httpsRedirectInput +
            ' | Package: ' +
            packageName +
            ' | Estimated Monthly cost:' +
            totalCostMonthly +
            ' | Estimated Annual cost:' +
            totalCost +
            ' | FormId:' +
            formId,
        },
      }),
    );
  };

  const buttonRow = () => {
    if (tileValueSelected === 'free') {
      return (
        <div>
          <ButtonSet className="ns1_pe__stack-button pa-mar4 gap-5">
            {tileValueSelected === 'free' && !notifToShow && (
              <Button
                id="request_btn"
                renderIcon={ArrowRight16}
                target="_blank"
                data-ibm-contact="scheduler-link"
                href="https://my.nsone.net/#/account/signup"
                disabled={
                  tileValueSelected === 'free' && notifToShow ? true : false
                }
                onClick={() => {
                  segmentsTracking('CTA Clicked', {
                    pageTitle: 'NS1 Price Estimator',
                    object: 'Pricing page',
                    resultValue: 'CTA clicked',
                    CTA: 'Sign up for free',
                    location: 'Estimator',
                    action: '',
                    field: '',
                  });
                }}
              >
                {useTranslation('signup_free_btn')}
              </Button>
            )}
            <Button
              kind="tertiary"
              id="demo_btn"
              renderIcon={ArrowRight16}
              onClick={() => {
                segmentsTracking('CTA Clicked', {
                  pageTitle: 'NS1 Pricing Page',
                  object: 'Pricing page',
                  resultValue: 'CTA clicked',
                  CTA: 'Request a demo',
                  location: 'Estimator',
                  action: '',
                  field: '',
                });
                initUrxForm(
                  environment,
                  URX_FORM.INSTANCE_ID,
                  formId,
                  urxlang as IBMLocale,
                  companyId,
                  shortcode,
                  setFormId,
                  'REQUEST_DEMO_FORM_ID',
                );
                setDrawerOpen(true);
                setCookie(formId);
              }}
            >
              {useTranslation('demo_button')}
            </Button>
          </ButtonSet>
        </div>
      );
    } else if (
      tileValueSelected === 'essentials' ||
      tileValueSelected === 'standard'
    ) {
      //here if we want to add for standard
      return (
        <div>
          <ButtonSet className="ns1_pe__stack-button pa-mar5 gap-5">
            {expertsLabs ||
              dedicatedDNS ||
              dnsInsight ||
              pulsar ||
              advSupport ||
              (showPrice && (
                <OmniChannel
                  id="ns1-pricing-estimator"
                  omniChannel={omnichannel}
                  setUrxForm={setDrawerOpen}
                  setOmniChannel={setOmniChannel}
                  environment={environment}
                  instanceId={URX_FORM.INSTANCE_ID}
                  formId={URX_FORM.FORM_ID}
                  urxlang={urxlang as IBMLocale}
                  pageTitle="NS1 Pricing Page"
                  object="Pricing Page"
                  location={String(location)}
                  tiles={tileValueSelected}
                  setCookie={setCookie}
                  useTranslation={useTranslation}
                  localeCode={localeCode}
                  ddoCountry={ddoCountry ? ddoCountry : 'us'}
                  userLocale={userLocale}
                />
              ))}
            <OmniChannelRequest
              id="ns1-pricing-estimator"
              OmniChannelRequest={omnichannelRequest}
              setUrxForm={setDrawerOpen}
              setOmniChannelRequest={setOmniChannelRequest}
              environment={environment}
              instanceId={URX_FORM.INSTANCE_ID}
              formId="FORM_ID_PE"
              formId2="REQUEST_DEMO_FORM_ID"
              urxlang={urxlang as IBMLocale}
              pageTitle={'NS1 Pricing Page'}
              object={'Pricing Page'}
              location={String(location)}
              tiles={tileValueSelected}
              setCookie={setCookie}
              useTranslation={useTranslation}
              userLocale={userLocale}
              setFormId={setFormId}
              shortcode={shortcode}
            />
          </ButtonSet>
        </div>
      );
    } else {
      return (
        <div>
          <ButtonSet className="ns1_pe__stack-button pa-mar5">
            <Button
              kind="primary"
              id="request_btn"
              renderIcon={ArrowRight16}
              onClick={() => {
                segmentsTracking('CTA Clicked', {
                  pageTitle: 'NS1 Pricing Page',
                  object: 'Pricing page',
                  resultValue: 'CTA clicked',
                  CTA: 'Book a consultation',
                  location: 'Estimator',
                  action: '',
                  field: '',
                });
                initUrxForm(
                  environment,
                  URX_FORM.INSTANCE_ID,
                  URX_FORM.FORM_ID,
                  urxlang as IBMLocale,
                  companyId,
                  shortcode,
                  setFormId,
                  'FORM_ID_PE',
                );
                setDrawerOpen(true);
                setCookie(formId);
              }}
            >
              {useTranslation('quote_text')}
            </Button>
          </ButtonSet>
        </div>
      );
    }
  };
  {
    /* Section to render result table -  */
  }

  const resultTable = () => {
    const pkgLabel =
      tileValueSelected === 'free'
        ? 'Free Developer'
        : tileValueSelected === 'essentials'
        ? 'Essentials'
        : tileValueSelected === 'standard'
        ? 'Standard'
        : 'Premium';
    const pkgInfo =
      tileValueSelected === 'free'
        ? convertToString(useTranslation('std_pkg_desc', true))
        : tileValueSelected === 'essentials'
        ? convertToString(useTranslation('est_pkg_desc', true))
        : tileValueSelected === 'standard'
        ? convertToString(useTranslation('std_desc_text', true))
        : convertToString(useTranslation('prem_pkg_desc', true));
    return (
      <>
        <div className="result">
          <h6>{useTranslation('recomm_text')}</h6>
          <div className="page_sub_heading">{pkgLabel}</div>
          <div className="price_table_comp__requirement x-pa-mar4">
            {pkgInfo}
          </div>
          <div>
            <div>
              <div className="bx--row">
                <div
                  className="bx--col-lg-16 bx--col-md-8 bx--col-sd-4"
                  style={{
                    display: `${
                      tileValueSelected === 'free' ||
                      tileValueSelected === 'premium'
                        ? 'none'
                        : 'block'
                    }`,
                  }}
                >
                  <div
                    id="widget.estimated-cost"
                    className="price_table_comp__requirement price_table_comp_color estmate-cost"
                  >
                    {useTranslation('estmCost')} <sup>1</sup>
                  </div>
                  <RadioButtonGroup
                    legendText=""
                    name="focusLevelPersonnel"
                    defaultSelected={'Monthly'}
                    valueSelected={priceAction}
                    ref={priceActionRef}
                    onChange={(e: string) => setPriceAction(String(e))}
                  >
                    <RadioButton
                      labelText={useTranslation('monthly')}
                      value="Monthly"
                      id="radio-1"
                    />
                    <RadioButton
                      labelText={useTranslation('yearly')}
                      value="Yearly"
                      id="radio-2"
                    />
                  </RadioButtonGroup>
                  <div className="price_table_comp__price">
                    {loading ? (
                      <SkeletonPlaceholder className="ns1_pe__sticky-nav-skeleton" />
                    ) : priceAction === 'Monthly' ? (
                      totalCostMonthly
                    ) : (
                      totalCost
                    )}
                    <div className="price_table_comp__disclaimer">
                      * {useTranslation('prcie_text')}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <hr className="thin_gray_option" />
          {renderFeaturesList(tileValueSelected, pkgLabel)}
          {buttonRow()}
        </div>
      </>
    );
  };

  const thresholdsMap = {
    queries: { free: 5, essentials: 80, standard: 1000 },
    records: { free: 500, essentials: 1000, standard: 10000 },
    chains: { free: 1, essentials: 1, standard: 100 },
    monitors: { free: 2, essentials: 2, standard: 100 },
    redirects: { free: 1, essentials: 5, standard: 50 },
  };

  function pickTier(
    value: number,
    thresholds: {
      free: number;
      essentials: number;
      standard: number;
      estStart?: number;
    },
  ) {
    const v = Number(value);
    if (v <= thresholds.free) return 'free';
    if (v <= thresholds.essentials) return 'essentials';
    if (v <= thresholds.standard) return 'standard';
    return 'premium';
  }

  interface tierProps {
    queriesInput?: string;
    recordsInput?: string;
    monitorsInput?: string;
    filterChainsInput?: string;
    httpsRedirectInput?: string;
    dataSet?: string;
    zoneBackUp?: string;
    expertsLabs?: string;
    dedicatedDNS?: string;
    dnsInsight?: string;
    pulsar?: string;
    advSupport?: string;
    spikeProtection?: string;
  }

  const [tierInfo, setTierInfo] = useState<tierProps>({});

  useEffect(() => {
    const updated: tierProps = {};
    if (dataSet) updated.dataSet = 'essentials';
    if (zoneBackUp) updated.zoneBackUp = 'standard';
    if (expertsLabs) updated.expertsLabs = 'premium';
    if (dedicatedDNS) updated.dedicatedDNS = 'premium';
    if (dnsInsight) updated.dnsInsight = 'premium';
    if (pulsar) updated.pulsar = 'premium';
    if (advSupport) updated.advSupport = 'premium';
    if (spikeProtection) updated.spikeProtection = 'standard';
    updated.queriesInput = pickTier(queriesInput, thresholdsMap.queries);
    updated.recordsInput = pickTier(recordsInput, thresholdsMap.records);
    updated.monitorsInput = pickTier(monitorsInput, thresholdsMap.monitors);
    updated.filterChainsInput = pickTier(
      filterChainsInput,
      thresholdsMap.chains,
    );
    updated.httpsRedirectInput = pickTier(
      httpsRedirectInput,
      thresholdsMap.redirects,
    );

    setTierInfo(updated);
  }, [
    dataSet,
    pulsar,
    dnsInsight,
    expertsLabs,
    spikeProtection,
    zoneBackUp,
    dedicatedDNS,
    advSupport,
    httpsRedirectInput,
    recordsInput,
    queriesInput,
    monitorsInput,
    filterChainsInput,
    tileValueSelected,
  ]);

  useEffect(() => {
    const tiersPresent = Object.values(tierInfo).filter(Boolean);
    if (tiersPresent.includes('premium')) {
      setTileValueSelected('premium');
      setPackageName('Premium');
    } else if (tiersPresent.includes('standard')) {
      setTileValueSelected('standard');
      setPackageName('Standard');
    } else if (tiersPresent.includes('essentials')) {
      setTileValueSelected('essentials');
      setPackageName('Essentials');
    } else if (tiersPresent.includes('free')) {
      setTileValueSelected('free');
      setPackageName('Free');
    }
  }, [tierInfo]);

  return (
    <div className="ns1_pe widget-styles">
      <Helmet>
        <script
          type="application/javascript"
          src={`https://www.ibm.com/account/ibmidutil/widget/js/loader.js`}
        ></script>
        <script
          type="application/javascript"
          src={`https://www.ibm.com/account/ibmidutil/widget/js/main.js`}
        ></script>
      </Helmet>
      {formComplete ? (
        <Thanks
          queriesInput={queriesInput}
          recordsInput={recordsInput}
          filterChainsInput={filterChainsInput}
          monitorsInput={monitorsInput}
          licensePriceMonthly={`${totalCostMonthly}`}
          licensePriceYearly={`${totalCost}`}
          packageName={packageName}
          bpName={bpName}
        />
      ) : (
        <div id="PASaaSGrid" className="x-bx--grid">
          {bpName ? (
            <BusinessPartner bpName={bpName} logo={logo} desc={desc} />
          ) : null}
          <div className="bx--row">
            <div
              className="bx--col-lg-3 bx--col-md-8 bx--col-sm-8 bx--offset-lg-13 margintop_1 pa-mar8"
              id="countryModal"
            >
              {/* Currently switched off - false to be removed once globalised */}

              {localExist && toggleCountry ? (
                <ModalWrapper
                  modalLabel={`Found country ${modalLocalCountry}. Access page in ${modalLocalLanguage} (${modalLocalCurrency}) `}
                  modalHeading={`The NS1 price estimator is available in ${modalLocalLanguage} with local currency ${modalLocalCurrency}`}
                  size={'sm'}
                  primaryButtonText={'Cool! Take me there'}
                  secondaryButtonText={''}
                  buttonTriggerText={`Access page in ${modalLocalLanguage}`}
                  handleOpen={() => {
                    segmentsTracking('CTA Clicked', {
                      pageTitle: 'NS1 Pricing Page',
                      object: 'Pricing page',
                      resultValue: 'CTA clicked',
                      CTA: 'Access Button for globalization',
                      location: 'Estimator',
                      action: '',
                      field: '',
                    });
                  }}
                  handleSubmit={() => {
                    localStorage.setItem('langLocale', selectedLocale);
                    (window.location.href =
                      'https://www.ibm.com/' +
                      selectedLocale +
                      '/products/ns1-connect/pricing'),
                      segmentsTracking('CTA Clicked', {
                        pageTitle: 'NS1 Pricing Page',
                        object: 'Pricing page',
                        resultValue: 'Submit button for globalization',
                        CTA: 'Submit button for globalization',
                        location: 'Estimator',
                        action: '',
                        field: '',
                      });
                    return true;
                  }}
                ></ModalWrapper>
              ) : null}
            </div>
          </div>
          <div className="bx--row">
            <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
              <div className="page_heading_2 mb-6 pl-5">
                {useTranslation('ns1_heading')}
              </div>
              <div className="page_body mb-3 pl-5">
                {useTranslation('ns1_subHeading')}
              </div>
              {/* User Part */}
              <div className="bx--row">
                <div className="bx--col-lg-11 bx--col-md-16 bx--col-sm-16 pa-mar2">
                  <div className="bx--row input-row-spacing">
                    <div className="bx--col-lg-7 bx--col-md-6 bx--col-sm-16 marginleft_1">
                      <Tooltip
                        onChange={() => {
                          segmentsTracking('CTA Clicked', {
                            pageTitle: 'NS1 Pricing Page',
                            object: 'Pricing page',
                            resultValue: 'CTA clicked',
                            CTA: 'DNS queries tooltip',
                            location: 'Estimator',
                            action: '',
                            field: '',
                          });
                        }}
                        direction={isSmall ? 'bottom' : 'right'}
                        tabIndex={0}
                        triggerText={
                          <span className="page_body mb-3">
                            {useTranslation('number_queries_text')}
                          </span>
                        }
                      >
                        <span className="slide_text">
                          {useTranslation('tooltip_queries_text')}
                        </span>
                      </Tooltip>
                      <NumberInput
                        id="queries"
                        size="md"
                        step={5}
                        allowEmpty={true}
                        ref={queriesRef}
                        onKeyDown={(e) => {
                          tileValueSelected === 'free' && e.preventDefault();
                        }}
                        invalidText={useTranslation('invalid_text1') as string}
                        className="number-input"
                        min={5}
                        value={queriesInput}
                        onChange={() => {
                          segmentsTracking('CTA Clicked', {
                            pageTitle: 'NS1 Pricing Page',
                            object: 'Pricing page',
                            resultValue: 'CTA clicked',
                            CTA: 'Total DNS queries in millions',
                            location: 'Estimator',
                            action: '',
                            field: '',
                          });
                          setQueriesInput(Number(queriesRef.current.value));
                        }}
                      />
                    </div>
                    <div className="bx--col-lg-7 bx--col-md-6 bx--col-sm-16 marginleft_1">
                      <Tooltip
                        onChange={() => {
                          segmentsTracking('CTA Clicked', {
                            pageTitle: 'NS1 Pricing Page',
                            object: 'Pricing page',
                            resultValue: 'CTA clicked',
                            CTA: 'DNS records tooltip',
                            location: 'Estimator',
                            action: '',
                            field: '',
                          });
                        }}
                        direction={isSmall ? 'bottom' : 'right'}
                        tabIndex={0}
                        triggerText={
                          <span className="page_body mb-3">
                            {useTranslation('number_records_text')}
                          </span>
                        }
                      >
                        <span className="slide_text">
                          {useTranslation('tooltip_records_text')}
                        </span>
                      </Tooltip>
                      <NumberInput
                        id="records"
                        size="md"
                        allowEmpty={true}
                        ref={recordsRef}
                        step={500}
                        onKeyDown={(e) => {
                          tileValueSelected === 'free' && e.preventDefault();
                          tileValueSelected === 'essentials' &&
                            e.preventDefault();
                        }}
                        invalidText={useTranslation('invalid_text2')}
                        min={500}
                        value={recordsInput}
                        onChange={() => {
                          segmentsTracking('CTA Clicked', {
                            pageTitle: 'NS1 Pricing Page',
                            object: 'Pricing page',
                            resultValue: 'CTA clicked',
                            CTA: 'Total DNS records',
                            location: 'Estimator',
                            action: '',
                            field: '',
                          });
                          setRecordsInput(Number(recordsRef.current.value));
                        }}
                      />
                    </div>
                  </div>
                  <div className="bx--row input-row-spacing">
                    <div className="bx--col-lg-7 bx--col-md-6 bx--col-sm-16 marginleft_1">
                      <Tooltip
                        onChange={() => {
                          segmentsTracking('CTA Clicked', {
                            pageTitle: 'NS1 Pricing Page',
                            object: 'Pricing page',
                            resultValue: 'CTA clicked',
                            CTA: 'Monitors tooltip',
                            location: 'Estimator',
                            action: '',
                            field: '',
                          });
                        }}
                        direction={isSmall ? 'bottom' : 'right'}
                        tabIndex={0}
                        triggerText={
                          <span className="page_body mb-3">
                            {useTranslation('number_monitors_text')}
                          </span>
                        }
                      >
                        <span className="slide_text">
                          {useTranslation('tooltip_monitors_text')}
                        </span>
                      </Tooltip>
                      <NumberInput
                        id="monitors"
                        size="md"
                        allowEmpty={true}
                        ref={monitorsRef}
                        step={1}
                        onKeyDown={(e) => {
                          tileValueSelected === 'free' && e.preventDefault();
                          tileValueSelected === 'essentials' &&
                            e.preventDefault();
                        }}
                        invalidText={useTranslation('invalid_text3') as string}
                        min={2}
                        value={monitorsInput}
                        onChange={() => {
                          segmentsTracking('CTA Clicked', {
                            pageTitle: 'NS1 Pricing Page',
                            object: 'Pricing page',
                            resultValue: 'CTA clicked',
                            CTA: 'Total Monitors',
                            location: 'Estimator',
                            action: '',
                            field: '',
                          });
                          setMonitorsInput(Number(monitorsRef.current.value));
                        }}
                      />
                    </div>
                    <div className="bx--col-lg-7 bx--col-md-6 bx--col-sm-16 marginleft_1">
                      <Tooltip
                        onChange={() => {
                          segmentsTracking('CTA Clicked', {
                            pageTitle: 'NS1 Pricing Page',
                            object: 'Pricing page',
                            resultValue: 'CTA clicked',
                            CTA: 'Filter chain tooltip',
                            location: 'Estimator',
                            action: '',
                            field: '',
                          });
                        }}
                        direction={isSmall ? 'bottom' : 'right'}
                        tabIndex={0}
                        triggerText={
                          <span className="page_body mb-3">
                            {useTranslation('number_filterchains_text')}
                          </span>
                        }
                      >
                        <span className="slide_text">
                          {useTranslation('tooltip_filterchain_text')}
                        </span>
                      </Tooltip>
                      <NumberInput
                        id="filter"
                        size="md"
                        allowEmpty={true}
                        ref={filterChainsRef}
                        onKeyDown={(e) => {
                          tileValueSelected === 'free' && e.preventDefault();
                          tileValueSelected === 'essentials' &&
                            e.preventDefault();
                        }}
                        invalidText={useTranslation('invalid_text4') as string}
                        className="number-input"
                        min={1}
                        value={filterChainsInput}
                        onChange={() => {
                          segmentsTracking('CTA Clicked', {
                            pageTitle: 'NS1 Pricing Page',
                            object: 'Pricing page',
                            resultValue: 'CTA clicked',
                            CTA: 'Total Filter Chains',
                            location: 'Estimator',
                            action: '',
                            field: '',
                          });
                          setFilterChainsInput(
                            Number(filterChainsRef.current.value),
                          );
                        }}
                      />
                    </div>
                  </div>
                  <div className="bx--row input-row-spacing last">
                    <div className="bx--col-lg-7 bx--col-md-6 bx--col-sm-16 marginleft_1">
                      <Tooltip
                        onChange={() => {
                          segmentsTracking('CTA Clicked', {
                            pageTitle: 'NS1 Pricing Page',
                            object: 'Pricing page',
                            resultValue: 'CTA clicked',
                            CTA: 'HTTP Redirect tooltip',
                            location: 'Estimator',
                            action: '',
                            field: '',
                          });
                        }}
                        direction={isSmall ? 'bottom' : 'right'}
                        tabIndex={0}
                        triggerText={
                          <span className="page_body mb-3">
                            {useTranslation('https_field_text')}
                          </span>
                        }
                      >
                        <span className="slide_text">
                          {useTranslation('https_field_info_text')}
                        </span>
                      </Tooltip>
                      <NumberInput
                        id="httpRedirect"
                        size="md"
                        allowEmpty={true}
                        ref={httpsRef}
                        step={1}
                        onKeyDown={(e) => {
                          tileValueSelected === 'free' && e.preventDefault();
                          tileValueSelected === 'essentials' &&
                            e.preventDefault();
                        }}
                        invalidText={useTranslation('invalid_text4') as string}
                        min={1}
                        value={httpsRedirectInput}
                        onChange={() => {
                          segmentsTracking('CTA Clicked', {
                            pageTitle: 'NS1 Pricing Page',
                            object: 'Pricing page',
                            resultValue: 'CTA clicked',
                            CTA: 'Total Https Redirect',
                            location: 'Estimator',
                            action: '',
                            field: '',
                          });
                          setHttpsRedirectInput(Number(httpsRef.current.value));
                        }}
                      />
                    </div>
                  </div>
                  <div className="page_body mb-3 pl-5">
                    <hr className="thin_gray_option" />
                    <strong>
                      {useTranslation('additional_features_heading')}
                    </strong>
                    <p className="additional_features_text">
                      {useTranslation('additional_features_text')}
                      {'   '}
                      &nbsp;&nbsp;
                      <Link
                        href={learnMoreLink}
                        renderIcon={ArrowRight16}
                        target="_blank"
                      >
                        {useTranslation('learn_more_text', true)
                          ? convertToString(
                              useTranslation('learn_more_text', true),
                            )
                          : 'Learn more'}
                      </Link>
                    </p>
                  </div>
                  <div className="bx--row cb-row-spacing">
                    <div className="bx--col-lg-5 bx--col-md-8 bx--col-sm-16 marginleft_1">
                      <div>
                        <Checkbox
                          labelText={convertToString(
                            useTranslation('checkbox1_text', true),
                          )}
                          id="data-set"
                          checked={dataSet}
                          onChange={() => setDataSet(!dataSet)}
                        />
                      </div>
                    </div>
                    <div className="bx--col-lg-5 bx--col-md-8 bx--col-sm-16 marginleft_1">
                      <Checkbox
                        labelText={convertToString(
                          useTranslation('checkbox2_text', true),
                        )}
                        id="zone-backup"
                        checked={zoneBackUp}
                        onChange={() => setZoneBackUp(!zoneBackUp)}
                      />
                    </div>
                    <div className="bx--col-lg-5 bx--col-md-8 bx--col-sm-16 marginleft_1">
                      <Checkbox
                        labelText={convertToString(
                          useTranslation('checkbox3_text', true),
                        )}
                        id="expert-labs"
                        checked={expertsLabs}
                        onChange={() => setExpertsLabs(!expertsLabs)}
                      />
                    </div>
                  </div>
                  <div className="bx--row cb-row-spacing">
                    <div className="bx--col-lg-5 bx--col-md-8 bx--col-sm-16 marginleft_1">
                      <Checkbox
                        labelText={convertToString(
                          useTranslation('checkbox4_text', true),
                        )}
                        id="dedicated-dns"
                        checked={dedicatedDNS}
                        onChange={() => setDedicatedDNS(!dedicatedDNS)}
                      />
                    </div>
                    <div className="bx--col-lg-5 bx--col-md-8 bx--col-sm-16 marginleft_1">
                      <Checkbox
                        labelText={convertToString(
                          useTranslation('checkbox5_text', true),
                        )}
                        id="dns-insight"
                        checked={dnsInsight}
                        onChange={() => setDnsInsight(!dnsInsight)}
                      />
                    </div>
                    <div className="bx--col-lg-5 bx--col-md-8 bx--col-sm-16 marginleft_1">
                      <Checkbox
                        labelText={convertToString(
                          useTranslation('checkbox8_text', true),
                        )}
                        id="spike-protection"
                        checked={spikeProtection}
                        onChange={() => setSpikeProtection(!spikeProtection)}
                      />
                    </div>
                  </div>
                  <div className="bx--row cb-row-spacing last-row">
                    <div className="bx--col-lg-5 bx--col-md-8 bx--col-sm-16 marginleft_1">
                      <Checkbox
                        labelText={convertToString(
                          useTranslation('checkbox7_text', true),
                        )}
                        id="advanced-support"
                        checked={advSupport}
                        onChange={() => setAdvSupport(!advSupport)}
                      />
                    </div>
                    <div className="bx--col-lg-5 bx--col-md-8 bx--col-sm-16 marginleft_1">
                      <Checkbox
                        labelText={convertToString(
                          useTranslation('checkbox6_text', true),
                        )}
                        id="pulsar"
                        checked={pulsar}
                        onChange={() => setPulsar(!pulsar)}
                      />
                    </div>
                  </div>
                  {tileValueSelected && (
                    <div id="footnote" className="div-only-lg">
                      <div className="bx--row">
                        <div className="bx--col-lg-15 marginleft_1">
                          <div>
                            <Footnote numNote={1} />
                          </div>
                          <div className="footnote_text">
                            <DisclaimerText
                              localeCode={localeCode}
                              product={'NS1 Price Estimator'}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <div className="bx--col-lg-5 bx--col-md-8 bx--col-sm-4">
                  {resultTable()}
                  {tileValueSelected !== 'premium' && (
                    <div id="footnote" className="div-only-media">
                      <div className="bx--row pa-mar6">
                        <div className="bx--col-lg-9">
                          <div>
                            <Footnote numNote={1} />
                          </div>
                          <div className="footnote_text">
                            <DisclaimerText
                              localeCode={localeCode}
                              product={'NS1 Price Estimator'}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  <FormModal
                    pageTitle="NS1 Pricing Page"
                    object="NS1 Pricing Page"
                    productTitle={'NS1 PE'}
                    pageUrl={''}
                    location={'Estimator'}
                    bpName={bpName}
                    logo={logo}
                    drawerOpen={drawerOpen}
                    setDrawerOpen={setDrawerOpen}
                    setCloseModal={setCloseModal}
                    closeModal={closeModal}
                  ></FormModal>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NS1PriceEstimatorNew;
